import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Ticket, PlusCircle, X, LogOut, User, ChevronUp } from 'lucide-react';
import { getRefreshToken, getUser, clearAuth } from '../../lib/auth';
import { apiPost } from '../../lib/api';
import { notifyLogout } from '../../lib/embed';

const navItems = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/tickets', label: 'Tickets', icon: Ticket },
  { href: '/tickets/new', label: 'New Ticket', icon: PlusCircle },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const user = getUser();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = user?.fullName
    ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'CU';

  const handleLogout = async () => {
    const refreshToken = getRefreshToken();
    try {
      await apiPost('/api/customer/auth/logout', refreshToken ? { refreshToken } : {});
    } catch {
      // Continue with local logout even if API fails
    }
    clearAuth();
    notifyLogout();
    navigate('/login', { replace: true });
  };

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r px-5 py-8 backdrop-blur-xl transition-transform duration-300 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          borderColor: 'var(--border-soft)',
          background: 'color-mix(in srgb, var(--surface) 85%, transparent)',
        }}
      >
        {/* Mobile close */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1.5 lg:hidden"
          style={{ color: 'var(--muted)' }}
        >
          <X size={20} />
        </button>

        {/* Logo */}
        <div className="flex items-center gap-3.5">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-2xl shadow-lg"
            style={{
              background: 'linear-gradient(135deg, var(--accent), var(--accent-deep))',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div>
            <p className="display-font text-base font-semibold" style={{ color: 'var(--ink)' }}>
              HelpDesk
            </p>
            <p className="label">Support Portal</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-10 flex flex-1 flex-col gap-1 overflow-y-auto">
          {navItems.map((item, i) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === '/'}
              onClick={onClose}
              className="nav-link group animate-in"
              style={({ isActive }) => ({
                animationDelay: `${i * 50}ms`,
                background: isActive ? 'var(--accent-soft)' : undefined,
                color: isActive ? 'var(--accent)' : undefined,
              })}
            >
              {({ isActive }) => (
                <span className="flex items-center gap-3">
                  <span
                    className="flex h-7 w-7 items-center justify-center rounded-lg transition-colors"
                    style={{
                      background: isActive ? 'var(--accent-soft)' : 'var(--warm)',
                      color: isActive ? 'var(--accent)' : 'var(--subtle)',
                    }}
                  >
                    <item.icon size={16} />
                  </span>
                  {item.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User card with expandable menu */}
        <div ref={menuRef} className="relative">
          {/* Expanded menu — slides up above the card */}
          <div
            className="overflow-hidden transition-all duration-200 ease-out"
            style={{
              maxHeight: menuOpen ? '250px' : '0',
              opacity: menuOpen ? 1 : 0,
              marginBottom: menuOpen ? '8px' : '0',
            }}
          >
            <div
              className="space-y-2 rounded-[14px] border p-3"
              style={{
                borderColor: 'var(--border)',
                background: 'var(--surface)',
              }}
            >
              {/* Profile link */}
              <NavLink
                to="/profile"
                onClick={() => { setMenuOpen(false); onClose(); }}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                style={{ color: 'var(--ink)' }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--warm)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <User size={15} />
                Profile
              </NavLink>

              {/* Divider */}
              <div style={{ height: '1px', background: 'var(--border-soft)' }} />

              {/* Sign out */}
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                style={{ color: 'var(--muted)' }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = 'var(--danger)';
                  e.currentTarget.style.background = 'var(--danger-soft)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = 'var(--muted)';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <LogOut size={15} />
                Sign out
              </button>
            </div>
          </div>

          {/* User card — clickable */}
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="w-full rounded-[14px] border p-3 text-left transition-colors"
            style={{
              borderColor: menuOpen ? 'var(--accent)' : 'var(--border)',
              background: 'linear-gradient(135deg, var(--surface), var(--warm))',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-xs font-semibold"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-soft), var(--warm))',
                  color: 'var(--accent)',
                }}
              >
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                  {user?.fullName || 'Customer'}
                </p>
                <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
                  Support Portal
                </p>
              </div>
              <ChevronUp
                size={16}
                className="shrink-0 transition-transform duration-200"
                style={{
                  color: 'var(--muted)',
                  transform: menuOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                }}
              />
            </div>
          </button>
        </div>
      </aside>
    </>
  );
}
